<template>
    <div>
        <!-- Mobile filter dialog -->
        <TransitionRoot as="template" :show="mobileFiltersOpen">
            <Dialog as="div" class="relative z-50 lg:hidden" @close="close">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300"
                                 enter-from="opacity-0" enter-to="opacity-100"
                                 leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
                                 leave-to="opacity-0">
                    <div class="fixed inset-0 bg-black bg-opacity-25"/>
                </TransitionChild>

                <div class="fixed inset-0 z-50 flex">
                    <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transition ease-in-out duration-300 transform"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <DialogPanel
                            class="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
                            <div class="fixed bottom-0 right-0 w-full max-w-xs p-2" v-if="mobileFiltersChanged">
                                <PrimaryButton class="w-full" @click="close">View results ({{ total }})</PrimaryButton>
                            </div>
                            <div class="flex items-center justify-between px-4">
                                <h2 class="text-lg font-medium text-gray-900">Filters</h2>
                                <button type="button"
                                        class="-mr-2 flex h-10 w-10 items-center justify-center rounded-md bg-white p-2 text-gray-400"
                                        @click="close">
                                    <span class="sr-only">Close menu</span>
                                    <PhX class="h-6 w-6" aria-hidden="true"/>
                                </button>
                            </div>
                            <div class="mt-2 mx-2 sm:mt-0">
                                <div class="-m-1 flex flex-wrap items-center">
                                    <span v-for="activeFilter in activeFilters" :key="activeFilter.value"
                                          class="m-1 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900">
                                        <span>{{ activeFilter.label }}: {{ activeFilter.value }}</span>
                                        <button
                                            @click="visitBrowse(activeFilter.href)"
                                            class="ml-1 p-0.5 inline-flex h-6 w-6 flex-shrink-0 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                                            <span class="sr-only">Remove filter for {{ activeFilter.label }}</span>
                                            <PhX class="w-full h-full"/>
                                        </button>
                                    </span>
                                </div>
                            </div>

                            <!-- Filters -->
                            <form class="mt-4 border-t border-gray-200">
                                <Disclosure as="div" v-for="section in filters" :key="section.id"
                                            class="border-t border-gray-200 px-4 py-6" v-slot="{ open }">
                                    <h3 class="-mx-2 -my-3 flow-root">
                                        <DisclosureButton
                                            class="flex w-full items-center justify-between bg-white px-2 py-3 text-gray-400 hover:text-gray-500">
                                            <span class="font-medium text-gray-900">{{ section.name }}</span>
                                            <span class="ml-6 flex items-center">
                                              <PhPlus v-if="!open" class="h-5 w-5" aria-hidden="true"/>
                                              <PhMinus v-else class="h-5 w-5" aria-hidden="true"/>
                                            </span>
                                        </DisclosureButton>
                                    </h3>
                                    <DisclosurePanel class="pt-6">
                                        <div class="grid grid-cols-6 gap-1" v-if="section.id === 'color'">
                                            <div v-for="option in section.options" :key="option.value"
                                                 @click="visitBrowse(option.href)"
                                                 class="cursor-pointer">
                                                <Tooltip :message="option.label">
                                                    <div class="w-full aspect-h-1 aspect-w-1">
                                                        <component :is="option.checked ? PhCheck : 'span'"
                                                                   class="check rounded-sm p-3"
                                                                   weight="bold"
                                                                   :class="option.value"/>
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <div class="space-y-6" v-else>
                                            <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                                 class="flex items-center"
                                                 @click="visitBrowse(option.href)">
                                                <input :id="`filter-mobile-${section.id}-${optionIdx}`"
                                                       :name="`${section.id}[]`" :value="option.value"
                                                       type="checkbox" :checked="option.checked"
                                                       class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"/>
                                                <label :for="`filter-mobile-${section.id}-${optionIdx}`"
                                                       class="ml-3 min-w-0 flex-1 text-gray-500">
                                                    {{ option.label }}
                                                    <template v-if="option.count">({{ option.count }})</template>
                                                </label>
                                            </div>
                                        </div>
                                    </DisclosurePanel>
                                </Disclosure>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <div class="w-full">
            <PageTitle :title="title">
                <div class="flex items-center">
                    <div class="mt-2 sm:ml-4 sm:mt-0 hidden lg:block">
                        <div class="-m-1 flex flex-wrap justify-end">
                          <span v-for="activeFilter in activeFilters" :key="activeFilter.value"
                                class="m-1 inline-flex items-center rounded-full border border-gray-200 bg-white py-1.5 pl-3 pr-2 text-sm font-medium text-gray-900">
                            <span>{{ activeFilter.label }}: {{ activeFilter.value }}</span>
                            <button
                                @click="visitBrowse(activeFilter.href)"
                                class="ml-1 p-0.5 inline-flex h-6 w-6 flex-shrink-0 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500">
                                <span class="sr-only">Remove filter for {{ activeFilter.label }}</span>
                                <PhX class="w-full h-full"/>
                            </button>
                          </span>
                        </div>
                    </div>
                    <Menu v-if="sortOptions.length > 0" as="div"
                          class="relative ml-5 inline-block text-left flex-shrink-0">
                        <div>
                            <MenuButton
                                class="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                Sort <span class="hidden sm:block">: {{ selectedSort?.name }}</span>
                                <PhCaretDown
                                    class="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                    aria-hidden="true"/>
                            </MenuButton>
                        </div>

                        <transition enter-active-class="transition ease-out duration-100"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition ease-in duration-75"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black
                ring-opacity-5 focus:outline-none">
                                <div class="py-1">
                                    <MenuItem v-for="option in sortOptions" :key="option.name"
                                              v-slot="{ active, close }">
                                        <Link :href="option.href"
                                              :preserve-state="true"
                                              @click="close"
                                              :class="[option.current ? 'font-medium text-gray-900' : 'text-gray-500', active ? 'bg-gray-100' : '',
                'block px-4 py-2 text-sm']">
                                            <PhCheck weight="bold" v-if="option.current" class="h-4 w-4 inline"/>
                                            {{ option.name }}
                                        </Link>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>

                    <button type="button" class="hidden sm:block -m-2 ml-3 p-2 text-gray-400 hover:text-gray-500">
                        <Tooltip message="Change grid size">
                            <PhGridFour weight="fill" class="h-6 w-6" aria-hidden="true" @click="toggleGrid"/>
                        </Tooltip>
                    </button>
                    <button type="button" class="-m-2 ml-2 p-2 text-gray-400 hover:text-gray-500 lg:hidden"
                            v-if="filters.length > 0"
                            @click="mobileFiltersOpen = true">
                        <Tooltip message="Filters">
                            <div class="flex">
                                <PhFunnel weight="fill" class="h-6 w-6" aria-hidden="true"/>
                                <span v-if="activeFilters?.length" class="ml-1 text-black">{{
                                        activeFilters.length
                                    }}</span>
                            </div>
                        </Tooltip>
                    </button>
                </div>
            </PageTitle>

            <Container>
                <section aria-labelledby="products-heading">
                    <h2 id="products-heading" class="sr-only">Products</h2>

                    <div class="flex flex-row gap-x-6 relative">
                        <!-- Filters -->
                        <transition name="filters"
                                    enter-active-class="transition-width ease-out duration-700"
                                    leave-active-class="transition-width ease-in duration-700"
                        >
                            <div v-if="showFilters" class="hidden lg:block w-1/4 flex-shrink-0 max-w-xs">
                                <form>
                                    <h3 class="sr-only">Products</h3>
                                    <Disclosure as="div" v-for="section in filters" :key="section.id"
                                                class="border-b border-gray-200 py-6 px-2" v-slot="{ open }">
                                        <h3 class="-my-3 flow-root">
                                            <DisclosureButton
                                                class="flex w-full items-center justify-between bg-white py-3 text-sm text-gray-400 hover:text-gray-500">
                                                <span class="font-medium text-gray-900">{{ section.name }}</span>
                                                <span class="ml-6 flex items-center">
                                            <PhPlus v-if="!open" class="h-5 w-5" aria-hidden="true"/>
                                            <PhMinus v-else class="h-5 w-5" aria-hidden="true"/>
                                        </span>
                                            </DisclosureButton>
                                        </h3>
                                        <DisclosurePanel class="pt-6">
                                            <div v-if="section.id === 'color'"
                                                 class="grid grid-cols-6 gap-2">
                                                <div v-for="option in section.options" :key="option.value"
                                                     @click="visitBrowse(option.href)"
                                                     class="cursor-pointer">
                                                    <Tooltip :message="option.label">
                                                        <div class="w-full aspect-h-1 aspect-w-1">
                                                            <component :is="option.checked ? PhCheck : 'span'"
                                                                       class="check rounded-sm p-3"
                                                                       weight="bold"
                                                                       :class="option.value"/>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            <div v-else
                                                 class="space-y-4">
                                                <div v-for="(option, optionIdx) in section.options" :key="option.value"
                                                     class="flex items-center"
                                                     @click="visitBrowse(option.href)">
                                                    <input :id="`filter-${section.id}-${optionIdx}`"
                                                           :name="`${section.id}[]`"
                                                           :value="option.value" type="checkbox"
                                                           :checked="option.checked"
                                                           class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"/>
                                                    <label :for="`filter-${section.id}-${optionIdx}`"
                                                           class="ml-3 text-sm text-gray-600">{{ option.label }}
                                                        <template v-if="option.count">({{ option.count }})</template>
                                                    </label>
                                                </div>
                                            </div>
                                        </DisclosurePanel>
                                    </Disclosure>
                                </form>
                            </div>
                        </transition>
                        <!-- Divider -->
                        <div class="border-l-2 border-gray-300 h-20 text-gray-600 relative hidden lg:block"
                             v-if="filters.length > 0">
                            <span @click="showFilters = !showFilters"
                                  class="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 cursor-pointer">
                                <transition enter-active-class="opacity-0 duration-700">
                                    <span
                                        class="absolute -rotate-90 text-sm bg-gray-700 px-2 py-0.5 -translate-x-12 rounded text-white"
                                        v-if="!showFilters">Filters</span>
                                </transition>
                                <template v-if="showFilters">
                                    <Tooltip message="Hide filters">
                                        <PhCaretCircleLeft weight="fill" class="w-6 h-6 bg-white"/>
                                    </Tooltip>
                                </template>
                                <template v-else>
                                    <PhCaretCircleRight weight="fill" class="w-6 h-6 bg-white"/>
                                </template>
                            </span>
                        </div>
                        <!-- Product grid -->
                        <div class="flex-grow max-w-[100%]">
                            <slot/>
                        </div>
                    </div>
                </section>
            </Container>
        </div>
    </div>
</template>

<style scoped>

.filters-enter-from,
.filters-leave-to {
    width: 0;
}

.check {
    /* Defaults */
    color: #FFF;
    background-size: cover;
}

.Cream {
    background-color: #FEFEF2 !important;
    border: 1px solid #000;
    color: #000;
}

.Metallic {
    background-image: url('@/assets/colors/metallic.jpg');
}

.Multi {
    background-image: url('@/assets/colors/multi.jpg');
}

.Aqua {
    background-color: #00f5ff !important;
}

.Black {
    background-color: black !important;
}

.Blue {
    background-color: blue !important;
}

.Brown {
    background-color: brown !important;
}

.Coral {
    background-color: coral !important;
}

.Gray {
    background-color: gray !important;
}

.Pink {
    background-color: pink !important;
}

.Green {
    background-color: green !important;
}

.Mint {
    background-color: mintcream !important;
    border: 1px solid #000;
    color: #000;
}

.Orange {
    background-color: orange !important;
}

.Purple {
    background-color: purple !important;
}

.Red {
    background-color: red !important;
}

.Tan {
    background-color: tan !important;
}

.Teal {
    background-color: teal !important;
}

.White {
    background-color: white !important;
    border: 1px solid #000;
    color: #000;
}

.Yellow {
    background-color: yellow;
}
</style>

<script setup>
import {ref, computed} from 'vue'
import {
    Dialog,
    DialogPanel,
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue'
import {
    PhCaretDown,
    PhFunnel,
    PhMinus,
    PhPlus,
    PhGridFour,
    PhX,
    PhCheck,
    PhCaretCircleLeft,
    PhCaretCircleRight,
} from '@phosphor-icons/vue';
import {Link, router} from '@inertiajs/vue3';
import PageTitle from "@/Components/PageTitle.vue";
import Tooltip from "@/Components/Tooltip.vue";
import Container from "@/Components/Container.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import metallic from '@/assets/colors/metallic.jpg';
import multi from '@/assets/colors/multi.jpg';

const props = defineProps({
    title: String,
    filters: Object,
    sortOptions: Object,
    activeFilters: Object | null,
    gridSize: Number,
    total: Number | null,
    initialShowFilters: Boolean,
});

const emit = defineEmits(['update:gridSize']);
const proxyGridSize = computed({
    get() {
        return props.gridSize;
    },
    set(val) {
        emit('update:gridSize', val);
    }
});

const mobileFiltersOpen = ref(false);
const mobileFiltersChanged = ref(false);
const selectedSort = computed(() => props.sortOptions.find(sort => {
    return sort.current;
}));

const showFilters = ref(props.filters.length > 0 && props.initialShowFilters);

function visitBrowse(href) {
    mobileFiltersChanged.value = true;
    router.visit(href, {preserveState: true, preserveScroll: true});
}

function toggleGrid() {
    proxyGridSize.value += 1;
}

function close() {
    mobileFiltersOpen.value = false
    mobileFiltersChanged.value = false;
}

</script>
